import { useQueries } from '@tanstack/react-query'
import { createDefaultPipeline } from '@ui/lib/reactFlow/helpers'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { ApolloError } from '@apollo/client'
import { Pipeline } from '@ui/__generated__/graphql'
import { Button, Modal, ToggleSwitch } from '@ui/components/core'
import { ManageAccess } from '@ui/components/elements/ManageAccess/ManageAccess'
import { showToast } from '@ui/components/elements/ToastManager'
import { ChevronDownIcon, DatableIcon } from '@ui/components/icons'
import { Spinner } from '@ui/components/loaders'
import { SideBar } from '@ui/components/navigation/SideBar'
import { DEFAULT_PIPELINE_WORKFLOW_NAME } from '@ui/constants/pipelinesWorkflow'
import { Member } from '@ui/interfaces'
import useAppNavigation from '@ui/lib/hooks/useAppNavigation'
import { useSavePipeline } from '@ui/lib/hooks/useSavePipeline'
import { removeTypename, uuid } from '@ui/lib/utils'
import { GetAllPipelinesQuery, UpdatePipelineMutation } from '@ui/loaders/workflow'
import { useAuth } from '@ui/providers/AuthProvider'
import { FeatureFlagName, useFeatureFlag } from '@ui/providers/FeatureFlagProvider'
import { RouteNamesEnum } from '@ui/router/routes'
import { orgService, teamService } from '@ui/services'
import { usePipelinesStore } from '@ui/stores'
import { useNodeAndEdgeStore, usePipelineModificationStore } from '@ui/stores/pipelinesWorkflow'
import clsx from 'clsx'

import { PipelineDropdown } from './PipelineDropdown'

export const PipelineNav = () => {
  const { pipelineId, instanceId } = useParams<{ pipelineId: string; instanceId: string }>()
  const { navigateTo } = useAppNavigation()
  const isQueryRailsLive = useFeatureFlag(FeatureFlagName.QueryRailsLive)

  const { currentMember, currentOrganization } = useAuth()

  const { data: allPipelines, loading } = GetAllPipelinesQuery()
  const { updateWorkflow } = useSavePipeline()
  const [updatePipeline] = UpdatePipelineMutation()

  const [{ data: teamsData }, { data: membersData }] = useQueries({
    queries: [
      {
        queryKey: ['teams'],
        queryFn: () => teamService.getTeams(currentMember as Member),
        enabled: isQueryRailsLive,
      },
      {
        queryKey: ['members'],
        queryFn: () => orgService.fetchMembers(currentOrganization?.id.toString() ?? ''),
        enabled: isQueryRailsLive,
      },
    ],
  })

  const { setUpdatePipeline } = useNodeAndEdgeStore(
    useShallow((state) => ({
      setUpdatePipeline: state.setUpdatePipeline,
    }))
  )

  const { pipelines, selectPipeline, selectedPipeline, setPipelines, setPipelineName } = usePipelinesStore(
    useShallow((state) => ({
      pipelines: state.pipelines,
      selectPipeline: state.selectPipeline,
      selectedPipeline: state.selectedPipeline,
      setPipelines: state.setPipelines,
      setPipelineName: state.setPipelineName,
    }))
  )

  const { isModified } = usePipelineModificationStore(
    useShallow((state) => ({
      isModified: state.isModified,
    }))
  )

  const [currentPipelineEnabled, setCurrentPipelineEnabled] = useState(selectedPipeline?.enabled ?? false)
  const [isPipelineDropdownOpen, setIsPipelineDropdownOpen] = useState(false)
  const [isNavigationDropdownOpen, setIsNavigationDropdownOpen] = useState(false)
  const [isManageAccessModalOpen, setIsManageAccessModalOpen] = useState(false)
  const newPipelineId = useMemo(() => uuid(), [])

  // add the new pipeline to the list of pipelines
  const navPipelines = useMemo(() => {
    const newPipelines = [...pipelines]
    const pipeline = createDefaultPipeline(true, newPipelineId)

    newPipelines.push(pipeline)

    return newPipelines
  }, [pipelines, newPipelineId])

  const disabledSaveButton = useMemo(() => {
    return !isModified && selectedPipeline?.name !== DEFAULT_PIPELINE_WORKFLOW_NAME
  }, [isModified, selectedPipeline])

  const handleSavePipeline = useCallback(async () => {
    try {
      const isNew = !pipelineId

      if (!selectedPipeline) {
        throw new Error('No pipeline selected')
      }

      const pipelineName = selectedPipeline.name

      if (pipelineName === DEFAULT_PIPELINE_WORKFLOW_NAME || pipelineName === '') {
        throw new Error('Pipeline name cannot be the default name or empty')
      }

      const { data } = await updateWorkflow({
        pipeline: selectedPipeline!,
        isNew,
        name: pipelineName,
        enabled: currentPipelineEnabled,
      })

      //@ts-expect-error TODO: fix this
      // should be handled in the useSavePipeline hook
      const updatedPipeline = data?.updatePipelineWorkflow as Pipeline
      const filteredPipelines = pipelines.filter((p) => p.id !== selectedPipeline?.id)

      // Update the pipeline in the store
      setPipelines([...filteredPipelines, updatedPipeline])
      selectPipeline(updatedPipeline)

      if (isNew) {
        navigateTo(RouteNamesEnum.EXISTING_PIPELINE, { pipelineId: updatedPipeline.id, instanceId })
      } else {
        setUpdatePipeline(true)
      }
    } catch (err) {
      const error = err as ApolloError

      showToast.error('Failed to save pipeline: ' + error.message)
    }
  }, [
    currentPipelineEnabled,
    instanceId,
    navigateTo,
    pipelineId,
    pipelines,
    selectPipeline,
    selectedPipeline,
    setPipelines,
    setUpdatePipeline,
    updateWorkflow,
  ])

  const handleSelectPipeline = useCallback(
    (pipelineId: string) => {
      const pipeline = pipelines.find((pipeline) => pipeline.id === pipelineId)

      if (pipelineId === newPipelineId) {
        navigateTo(RouteNamesEnum.NEW_PIPELINE)
        setPipelineName(DEFAULT_PIPELINE_WORKFLOW_NAME)
      } else {
        navigateTo(RouteNamesEnum.EXISTING_PIPELINE, { pipelineId, instanceId })
        selectPipeline(pipeline!)
        setCurrentPipelineEnabled(pipeline?.enabled || false)
        setPipelineName(pipeline?.name || '')
        setUpdatePipeline(true)
      }

      setIsPipelineDropdownOpen(false)
    },
    [pipelines, newPipelineId, navigateTo, instanceId, selectPipeline, setUpdatePipeline, setPipelineName]
  )

  const toggleEnablePipeline = useCallback(
    async (pipeline: Pipeline, enabled: boolean) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { nodes, edges, assignedSources, ...pipelineRest } = pipeline

      // pipeline doesn't have an id, so we can't update it so we just update the state :D
      if (!pipelineId) {
        setCurrentPipelineEnabled(enabled)
        return
      }

      try {
        const { data } = await updatePipeline({
          variables: {
            pipeline: {
              ...removeTypename(pipelineRest),
              enabled,
            },
          },
        })

        setCurrentPipelineEnabled(enabled)
        setUpdatePipeline(true)

        const updatedPipeline = data?.updatePipeline as Pipeline
        const filteredPipelines = pipelines.filter((p) => p.id !== pipeline.id)

        setPipelines([...filteredPipelines, updatedPipeline])

        showToast.success(`Pipeline ${pipeline.name} ${enabled ? 'enabled' : 'disabled'}`)
      } catch (error) {
        showToast.error('Failed to save pipeline')
      }
    },
    [pipelineId, pipelines, setPipelines, setUpdatePipeline, updatePipeline]
  )

  useEffect(() => {
    const pipelines = allPipelines?.allPipelineWorkflows
    if (!pipelines) return

    setPipelines(pipelines)

    const pipelineToSelect = pipelineId
      ? pipelines.find((pipeline) => pipeline.id === pipelineId)
      : pipelines.find((pipeline) => pipeline.name === DEFAULT_PIPELINE_WORKFLOW_NAME)

    if (pipelineToSelect) {
      selectPipeline(pipelineToSelect)
    }
  }, [allPipelines, pipelineId, selectPipeline, setPipelines])

  useEffect(() => {
    setCurrentPipelineEnabled(selectedPipeline?.enabled ?? false)
  }, [selectedPipeline])

  return (
    <div className="flex flex-row items-center justify-between bg-gray-800 px-4 py-2">
      {/* Left*/}
      <div
        className={clsx(
          'relative flex cursor-pointer flex-row items-center rounded-md p-2 transition-all duration-300 hover:bg-gray-900',
          {
            'bg-gray-900': isNavigationDropdownOpen,
          }
        )}
        onClick={() => setIsNavigationDropdownOpen(!isNavigationDropdownOpen)}>
        <DatableIcon className="size-6 fill-white" />
        <ChevronDownIcon className="size-4 stroke-white" />

        {isNavigationDropdownOpen && (
          <div className="absolute left-0 top-12 z-40 h-auto w-48 rounded-md shadow-md">
            <SideBar isDropdownMenu />
          </div>
        )}
      </div>

      {/* Middle */}
      <div className="relative h-full">
        {loading || !pipelines || !selectedPipeline ? (
          <Spinner />
        ) : (
          <PipelineDropdown
            openDropdown={() => setIsPipelineDropdownOpen(!isPipelineDropdownOpen)}
            currentPipeline={selectedPipeline!}
            pipelines={navPipelines}
            isOpen={isPipelineDropdownOpen}
            handleSelectPipeline={handleSelectPipeline}
            updatePipelineName={setPipelineName}
            newPipelineId={newPipelineId}
          />
        )}
      </div>

      {/* Right */}
      <div className="flex flex-row items-center space-x-2">
        <p className="text-xs font-medium text-white">Enable</p>
        <ToggleSwitch
          checked={currentPipelineEnabled}
          onChange={() => toggleEnablePipeline(selectedPipeline!, !currentPipelineEnabled)}
        />
        {isQueryRailsLive && (
          <Button
            className="bg-white"
            size="small"
            variant="secondary"
            onClick={() => setIsManageAccessModalOpen(true)}>
            Manage Access
          </Button>
        )}
        <Button size="small" variant="primary-red" disabled={disabledSaveButton} onClick={handleSavePipeline}>
          Save
        </Button>
      </div>

      {pipelineId && isQueryRailsLive && (
        <Modal isOpen={isManageAccessModalOpen} onClose={() => setIsManageAccessModalOpen(false)} title="Manage Access">
          <ManageAccess
            teams={teamsData ?? []}
            members={membersData ?? []}
            currentMember={currentMember!}
            pipelineId={pipelineId}
          />
        </Modal>
      )}
    </div>
  )
}
